/**
 * Lead attribution helper.
 *
 * - Captures UTM params + landing page + referrer on first load
 *   and stores them in sessionStorage so they survive in-app navigation.
 * - Infers a pillar_context from the current pathname
 *   (rpo | bpo | kpo | ai | services | industries | case-studies |
 *    about | careers | contact | home).
 * - Exposes getAttribution() to spread into every form POST body.
 */

const STORAGE_KEY = "a4a_attribution_v1";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

function readStored() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function writeStored(obj) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* ignore */
  }
}

/** Call once on app boot (mount). Safe to call again — merges, never overwrites. */
export function captureAttributionOnLoad() {
  if (typeof window === "undefined") return;
  const stored = readStored();
  const url = new URL(window.location.href);

  const next = { ...stored };

  // First-touch UTM: only set if not already stored
  UTM_KEYS.forEach((k) => {
    const v = url.searchParams.get(k);
    if (v && !next[k]) next[k] = v.slice(0, 200);
  });

  if (!next.landing_page) {
    next.landing_page = (url.pathname + url.search).slice(0, 500);
  }
  if (!next.referrer && document.referrer) {
    next.referrer = document.referrer.slice(0, 500);
  }

  writeStored(next);
}

/** Infer pillar from the current URL path. */
export function inferPillar(pathname = window.location.pathname) {
  const p = (pathname || "").toLowerCase();
  if (p.includes("/services/rpo")) return "rpo";
  if (p.includes("/services/bpo")) return "bpo";
  if (p.includes("/services/kpo")) return "kpo";
  if (p.startsWith("/ai")) return "ai";
  if (p.startsWith("/services")) return "services";
  if (p.startsWith("/industries")) return "industries";
  if (p.startsWith("/case-studies")) return "case-studies";
  if (p.startsWith("/about")) return "about";
  if (p.startsWith("/careers")) return "careers";
  if (p.startsWith("/contact")) return "contact";
  return "home";
}

/**
 * Returns attribution fields for a lead POST payload.
 * Always includes pillar_context (derived at submit-time) and, if present,
 * utm_* / landing_page / referrer captured on first load.
 */
export function getAttribution() {
  const stored = readStored();
  return {
    pillar_context: inferPillar(),
    utm_source: stored.utm_source || null,
    utm_medium: stored.utm_medium || null,
    utm_campaign: stored.utm_campaign || null,
    utm_content: stored.utm_content || null,
    utm_term: stored.utm_term || null,
    landing_page: stored.landing_page || null,
    referrer: stored.referrer || null,
  };
}
